import { useState, useRef } from "react";
import { useToggle } from "react-use";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { Person } from "schema-dts";
import { sortBy } from "lodash";
import { Badge, Box, Grid, Stack, Typography } from "@mui/material";
import PageHeader from "../../components/PageHeader";
import { colors } from "../../theme";
import { Header } from "../../components/Text";
import ProductCard from "../../components/ProductCard";
import { isNextMonth } from "../../helpers/time";
import { Loader } from "../../components/Loader";
import Button from "../../components/Button";
import { useAppState } from "../../state";
import useLoadMoreOnScroll from "../../hooks/useLoadMoreOnScroll";
import Switch from "../../components/Switch";
import FiltersDrawer from "../Shop/Filters/Drawer";
import FiltersDesktopDrawer from "../Shop/Filters/DesktopDrawer";
import { FiltersButton } from "../Shop/styles";
import { Icon } from "../../components/Icon";
import FrameBadge from "../../components/FrameBadges/FrameBadge";
import FrameBadges from "../../components/FrameBadges";
import { Frame } from "../../types/product";
import BottomSheet from "../../components/BottomSheet";
import { Margin } from "../../components/Layout";
import { useGetArtistQuery } from "../../generated/firebase";
import { adminClient, firebaseClient } from "../../helpers/apolloClients";
import { useGetAdminProductsQuery } from "../../generated/graphql";

const Artist = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [selectedFrame, setSelectedFrame] = useState(Frame.Natural);
  const [openFrameBar, setOpenFrameBar] = useState(false);
  const { selectedCountry, isMobileScreen, navHeight } = useAppState();
  const style = searchParams.get("style");
  const subject = searchParams.get("subject");
  const orientation = searchParams.get("orientation");
  const material = searchParams.get("material");
  const styles = style?.split(",") || [];
  const subjects = subject?.split(",") || [];
  const filtersQuery =
    [...styles, ...subjects].map((tag) => `tag:gm.${tag}`).join(" OR ") +
    (orientation ? ` metafields.custom.orientation:${orientation}` : "") +
    (material === "canvas" ? ` metafields.custom.available_for_canvas:true` : "");
  const { data: artistData, loading: loadingArtist } = useGetArtistQuery({
    client: firebaseClient,
    variables: {
      id: id || "",
    },
    skip: !id,
  });
  const artist = artistData?.artist;
  const { data, loading, fetchMore } = useGetAdminProductsQuery({
    variables: {
      limit: 100,
      query: `status:ACTIVE vendor:${artist?.id} ${filtersQuery}`,
      country: selectedCountry,
    },
    client: adminClient,
    skip: !artist,
  });
  const products = sortBy(data?.products.nodes, (product) => Number(product.artistPosition?.value));
  const hasMore = data?.products.pageInfo.hasNextPage || false;
  const afterCursor = data?.products.pageInfo.endCursor || "";
  const filters = ["style", "subject"];
  let filterCount = 0;
  searchParams.forEach((value, key) => filters.includes(key) && value && filterCount++);
  const availableTags = (products?.flatMap((product) => product.tags) || []).map((tag) => tag.replace("gm.", ""));

  const loadMore = async () => {
    await fetchMore({
      variables: {
        afterCursor,
      },
    });
  };

  const { loadingMore } = useLoadMoreOnScroll(ref, loadMore, hasMore, loading);

  const saveScrollPosition = () => {
    window.sessionStorage.setItem("scrollPosition", window.scrollY.toString());
  };

  const onViewChange = (value: string) => {
    searchParams.set("view", value);
    setSearchParams(searchParams, { preventScrollReset: true });
  };

  const clearFilters = () => {
    searchParams.delete("style");
    searchParams.delete("subject");
    setSearchParams(searchParams);
  };

  if (loadingArtist) {
    return (
      <Box width="100%" style={{ height: `calc(80vh - ${navHeight}px)` }}>
        <Stack alignItems="center" justifyContent="center" style={{ height: "100%" }}>
          <Loader />
        </Stack>
      </Box>
    );
  }

  if (!artist)
    return (
      <Box padding="40px">
        <Stack gap={2} justifyContent="center" alignItems="center">
          <Typography variant="h2" component="h1">
            Artist not found
          </Typography>
          <Link to="/artists">
            <Button secondary>View all artists</Button>
          </Link>
        </Stack>
      </Box>
    );

  const artistName = artist.firstName + " " + artist.lastName;
  const description = artist.about || artistName + "'s collection on GoodMood Prints";
  const image = artist.image.large;
  const url = "https://www.goodmoodprints.com/artists/" + artist.permalink;

  return (
    <>
      <Helmet
        script={[
          helmetJsonLdProp<Person>({
            "@context": "https://schema.org",
            "@type": "Person",
            name: artist.firstName + " " + artist.lastName,
            image,
            description,
            url,
            worksFor: {
              "@type": "Organization",
              name: "GoodMood Prints",
              url: "https://www.goodmoodprints.com",
            },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: `${artistName} Prints`,
              itemListElement: products.map((product) => ({
                "@type": "Product",
                name: product.title,
                image: product.images.nodes[0]?.src,
                url: `https://www.goodmoodprints.com/products/${product.handle}`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "GBP",
                  price: product.priceRangeV2.minVariantPrice.amount,
                  availability: "https://schema.org/InStock",
                },
              })),
            },
          }),
        ]}
      >
        <title>{artistName + "'s Art Collection"} | GoodMood Prints</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />

        <meta property="og:title" content={artistName} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="GoodMood Prints" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={artistName} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />

        <meta itemProp="image" content={image} />
      </Helmet>

      <Box minHeight={!artist || !products || loading ? `${window.sessionStorage.getItem("scrollPosition")}px` : "auto"}>
        <PageHeader
          header={`${artist.firstName} ${artist.lastName}`}
          background={artist.backgroundColour || colors.mint}
          image={image}
        >
          {artist.about}
        </PageHeader>
        <Margin margin={8} />
        <Stack
          position="sticky"
          top={navHeight}
          zIndex={1}
          gap={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={{ xs: 2, md: 5 }}
          paddingY={1}
          bgcolor={colors.white}
        >
          {isMobileScreen ? (
            <Badge badgeContent={filterCount} color="secondary">
              <FiltersButton onClick={toggleShowFilters}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Icon icon="filters" fill={colors.black} size={20} />
                </Stack>
              </FiltersButton>
            </Badge>
          ) : (
            <Stack minWidth={200} alignItems="flex-start" justifyContent="center">
              <FiltersButton onClick={toggleShowFilters}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontSize={12}>Filters{filterCount > 0 ? ` (${filterCount})` : ""}</Typography>
                  <Icon icon="filters" fill={colors.black} size={20} />
                </Stack>
              </FiltersButton>
            </Stack>
          )}

          <Switch
            options={[
              {
                label: "Artwork",
                value: "artwork",
              },
              {
                label: "Interior",
                value: "interior",
              },
            ]}
            fullWidth={isMobileScreen}
            selected={searchParams.get("view") || "artwork"}
            onChange={onViewChange}
          />

          {isMobileScreen ? (
            <Stack
              onClick={() => setOpenFrameBar(true)}
              component="button"
              position="relative"
              bgcolor={colors.white}
              border={`2px solid ${colors.cardGrey}`}
              borderRadius={100}
              padding={0.25}
            >
              <FrameBadge frame={selectedFrame} size={30} />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" minWidth={200}>
              <FrameBadges selectedFrame={selectedFrame} setSelectedFrame={setSelectedFrame} size="medium" />
            </Stack>
          )}
        </Stack>

        {isMobileScreen ? (
          <FiltersDrawer
            isOpen={showFilters}
            onClose={toggleShowFilters}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            hideSortBy
            filterGroupsToHide={["colour", "month", "size", "medium"]}
            availableTags={availableTags}
            selectedCountry={selectedCountry}
          />
        ) : (
          <FiltersDesktopDrawer
            isOpen={showFilters}
            onClose={toggleShowFilters}
            filterCount={filterCount}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            filterGroupsToHide={["colour", "month", "size", "medium"]}
            availableTags={availableTags}
            selectedCountry={selectedCountry}
          />
        )}

        <Box ref={ref} padding={isMobileScreen ? "8px 0 24px" : "8px 40px 40px"}>
          {products && !loading ? (
            products.length === 0 ? (
              filterCount > 0 ? (
                <Box padding="32px 24px 400px">
                  <Stack alignItems="center" gap={2}>
                    <Typography>No pieces with selected filters</Typography>
                    <Button size="medium" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box padding="32px 24px 400px">
                  <Stack alignItems="center">
                    <Header type="h2" margin="0 0 16px 0" align="center">
                      Nothing uploaded yet
                    </Header>
                    <Link
                      to={{
                        pathname: "/shop",
                        search: "?collection=true",
                      }}
                    >
                      <Button secondary>Explore the collection</Button>
                    </Link>
                  </Stack>
                </Box>
              )
            ) : (
              <>
                <Grid container spacing={{ xs: 1, md: 2 }} rowGap={{ xs: 4, md: 9 }}>
                  {products.map((product, index) => (
                    <Grid item xs={6} md={3} xl={3} xxl={2} xxxl={1.5} key={product.id} onClick={saveScrollPosition}>
                      <ProductCard
                        product={product}
                        index={index}
                        frame={selectedFrame}
                        nextMonths={isNextMonth(product.productType)}
                        indent={isMobileScreen}
                        style={(searchParams.get("view") as "artwork" | "interior") || "artwork"}
                        canvas={searchParams.get("material") === "canvas"}
                      />
                    </Grid>
                  ))}
                </Grid>
                {loadingMore ? (
                  <Loader />
                ) : hasMore ? (
                  <Stack width="100%" alignItems="center" padding={5}>
                    <Button onClick={loadMore}>Load more</Button>
                  </Stack>
                ) : null}
              </>
            )
          ) : (
            <Loader />
          )}
        </Box>
      </Box>

      {isMobileScreen && (
        <BottomSheet open={openFrameBar} onClose={() => setOpenFrameBar(false)}>
          <Typography>Frame colour</Typography>
          <FrameBadges
            selectedFrame={selectedFrame}
            setSelectedFrame={(frame: Frame) => {
              setSelectedFrame(frame);
              setOpenFrameBar(false);
            }}
            size="large"
            showLabels
          />
        </BottomSheet>
      )}
    </>
  );
};

export default Artist;
